import { ReactElement, ReactNode, useState } from 'react';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { IconProps } from '@components/icons';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconTrash } from '@components/icons/trash';
import { Modal, hideModal, useModal } from '@components/modal';
import { ModalVariant } from '@components/modal/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { XOR } from '@utils/types';

type DescriptionType = XOR<{ description: string | ReactNode }, { itemTitle: string }>;

export type DeleteModalProps = DescriptionType & {
  onConfirm(): void | Promise<unknown>;
  title?: string;
  hasUserConfirmation?: boolean;
  children?: ReactNode;
  buttonText?: string;
  buttonIcon?: ReactElement<IconProps>;
  hideCannotBeUndone?: boolean;
  hideButton?: boolean;
};

export const useDeleteModal = (variant?: ModalVariant) =>
  useModal(DeleteModal, { variant: variant ?? 'REGULAR' });

const DeleteModal = ({
  title,
  onConfirm,
  hasUserConfirmation = false,
  description,
  itemTitle,
  children,
  buttonText,
  buttonIcon,
  hideCannotBeUndone = false,
  hideButton = false,
}: DeleteModalProps) => {
  const { t } = useScopedTranslate(locale);
  const [isConfirm, setIsConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      hideModal();
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={title ?? t('title')} icon={<IconFormIndicatorWarning />} />
      <Modal.Content>
        <Stack space={6}>
          <Stack space={4}>
            <Stack>
              <Text text={description ?? t('areYouSure', { itemTitle: itemTitle ?? '' })} />
              {!hideCannotBeUndone && <Text color="red100" text={t('cannotBeUndone')} />}
            </Stack>
            {children}
          </Stack>
          {hasUserConfirmation && (
            <>
              <Separator />
              <Stack space={4}>
                <Text font="semibold14" text={t('pleaseConfirm')} />
                <Checkbox label={t('iUnderstand')} onChange={setIsConfirm} />
              </Stack>
            </>
          )}
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" space={4} justify="flex-end">
          <Button variant="secondary" onClick={hideModal} text={t('core.cancel')} />
          {!hideButton && (
            <Button
              isDisabled={hasUserConfirmation && !isConfirm}
              onClick={handleConfirm}
              disabled={isLoading}
              variant="primary-red"
              iconLeft={buttonIcon ?? <IconTrash />}
              text={buttonText ?? t('core.delete')}
            />
          )}
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

export const locale = {
  en: {
    title: 'Warning',
    areYouSure: 'Are you sure you want to remove {itemTitle}?',
    cannotBeUndone: 'This can not be undone.',
    pleaseConfirm: 'Please confirm',
    iUnderstand: 'I understand',
  },
  is: {
    title: 'Viðvörun',
    areYouSure: 'Ertu viss um að þú viljir fjarlægja {itemTitle}?',
    cannotBeUndone: 'Þessi aðgerð er óafturkræf',
    pleaseConfirm: 'Vinsamlega staðfestið',
    iUnderstand: 'Ég samþykki',
  },
};
